import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { NetworkGroupData as NetworkGroup } from '@/generated/types/navigator/types.gen';
import { setActiveGroupName } from './analysisMapSlice';
import { fetchNetworkGroups } from './networkGroupSlice';
import { isFilterEmpty, pickFilterCriteria } from '@/features/filterHelpers';
import { UNGROUPED } from '@/helpers/networkGroups';
import type { GroupId } from './types';

export interface FuelCategoryThresholds {
  grass: string[];
  shrub: string[];
  timber_litter: string[];
  slash_blowdown: string[];
  non_burnable: string[];
}

export interface FilterCriteria {
  network: string;
  groups: Record<string, (string | null)[] | undefined>;
  voltageInKv: string[];
  phasingType: string[];
  encroachmentRatings: string[];
  species: string[];
  wildfireRatings: string[];
  redFlagWarnings: string[];
  hasHazardTrees: boolean;
  hasStrikeTrees: boolean;
  hasCriticalTrees: boolean;
  encroachmentType: string[];
  tags: string[];
  priorityRatings: string[];
  wildfirePriorityRatings: string[];
  workStatus: string[];
  updatedAtYears: string[];
  fuelHazardRatings: string[];
  fuelCategoryThresholds: FuelCategoryThresholds;
}

const emptyFuelCategoryThresholds: FuelCategoryThresholds = {
  grass: [],
  shrub: [],
  timber_litter: [],
  slash_blowdown: [],
  non_burnable: [],
};

/**
 * UI and internal state - does not affect filtering logic
 */
interface FilterUIState {
  /** Is anything in the filter set? */
  isSelected: boolean;
  /** Increments each time the filter is updated */
  changeCount: number;
  activeGroupName: string | null;
  spanNetworkGroupId: GroupId | null;
  previousFilterQuery: object;
}

// Combined state for the slice
export interface FilterState extends FilterCriteria, FilterUIState {}

// Empty filter state
interface EmptyFilterCriteria extends FilterCriteria, FilterUIState {}

export const emptyFilterCriteria: FilterCriteria = {
  network: 'all',
  groups: {},
  voltageInKv: [],
  phasingType: [],
  encroachmentRatings: [],
  species: [],
  wildfireRatings: [],
  hasHazardTrees: false,
  hasStrikeTrees: false,
  hasCriticalTrees: false,
  encroachmentType: [],
  tags: [],
  priorityRatings: [],
  wildfirePriorityRatings: [],
  workStatus: [],
  updatedAtYears: [],
  redFlagWarnings: [],
  fuelHazardRatings: [],
  fuelCategoryThresholds: { ...emptyFuelCategoryThresholds },
};

const emptyUIState: FilterUIState = {
  isSelected: false,
  changeCount: 0,
  activeGroupName: null,
  spanNetworkGroupId: null,
  previousFilterQuery: {},
};

const emptyFilterState: EmptyFilterCriteria = {
  ...emptyFilterCriteria,
  ...emptyUIState,
};

export const initialState: FilterState = {
  ...emptyFilterState,
  previousFilterQuery: { ...emptyFilterCriteria },
};

const filterSlice = createSlice({
  name: 'filterAnalysis',
  initialState,
  reducers: {
    clearQuery: (state) => ({
      ...initialState,
      changeCount: state.changeCount + 1,
      isActive: false,
    }),
    setQuery: (state, action) => {
      const {
        network,
        groups,
        encroachmentRatings,
        species,
        wildfireRatings,
        hasHazardTrees,
        hasStrikeTrees,
        hasCriticalTrees,
        encroachmentType,
        tags,
        priorityRatings,
        wildfirePriorityRatings,
        workStatus,
        voltageInKv,
        phasingType,
        updatedAtYears,
        redFlagWarnings,
        fuelHazardRatings,
        fuelCategoryThresholds,
      } = action.payload;

      // Save the previous filter criteria for comparisons
      state.previousFilterQuery = pickFilterCriteria(state);
      state.network = network;
      state.voltageInKv = voltageInKv;
      state.phasingType = phasingType;
      state.groups = groups;
      state.encroachmentRatings = encroachmentRatings;
      state.species = species;
      state.wildfireRatings = wildfireRatings;
      state.redFlagWarnings = redFlagWarnings;
      state.hasHazardTrees = hasHazardTrees;
      state.hasStrikeTrees = hasStrikeTrees;
      state.hasCriticalTrees = hasCriticalTrees;
      state.encroachmentType = encroachmentType;
      state.tags = tags;
      state.priorityRatings = priorityRatings;
      state.wildfirePriorityRatings = wildfirePriorityRatings;
      state.workStatus = workStatus;
      state.updatedAtYears = updatedAtYears;
      state.fuelHazardRatings = fuelHazardRatings;
      state.fuelCategoryThresholds = fuelCategoryThresholds;
      state.isSelected = !isFilterEmpty(state);
      state.changeCount = state.changeCount + 1;
    },
    toggleNetwork: (state, action) => {
      const targetNetwork = action.payload.network;
      state.network = state.network === targetNetwork ? 'all' : targetNetwork;
      state.isSelected = !isFilterEmpty(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setActiveGroupName, (state, action) => {
      if (action.payload !== undefined) {
        const activeGroupName = action.payload;
        return {
          ...state,
          activeGroupName,
        };
      }
    });
    builder.addMatcher(isAnyOf(fetchNetworkGroups), (state, action) => {
      if (action.payload !== undefined) {
        const spanNetworkGroup = action.payload.find(
          (group: NetworkGroup) => group.name === 'level3'
        );

        return {
          ...state,
          spanNetworkGroupId: spanNetworkGroup?.id ?? UNGROUPED.id,
        };
      }
    });
  },
});

export const { clearQuery, setQuery, toggleNetwork } = filterSlice.actions;

export default filterSlice.reducer;
